'use client'

import Link from 'next/link'
import React, { useState } from 'react'

import { Eye, EyeClosed } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Input, InputWrapper } from '@/components/ui/input'

import { useLocale } from '@/hooks/use-locale'

import { cn, isFieldInvalid } from '@/utils'

import { FieldInfo } from './field-info'
import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

export type PasswordProps = {
  forgotPassword?: boolean
  label?: string
  required?: boolean
} & React.ComponentProps<typeof Input>

export const Password: React.FC<PasswordProps> = ({
  className,
  forgotPassword,
  ...props
}) => {
  const field = useFieldContext<string>()
  const isInvalid = isFieldInvalid(field)
  const t = useTranslations('components.form.password')
  const [isVisible, setIsVisible] = useState(false)
  const { direction } = useLocale()

  const toggleVisibility = () => setIsVisible(!isVisible)

  return (
    <FieldWrapper
      classNames={{ childrenWrapper: 'relative' }}
      field={field}
      label={props.label}
      required={props.required}
      showFieldInfo={false}
    >
      <div className="relative flex flex-col gap-2" dir={direction}>
        <InputWrapper
          className="h-[42px] rounded-md !px-3.5 !py-2"
          data-invalid={isInvalid || undefined}
        >
          <Input
            aria-invalid={isInvalid || undefined}
            className={cn(className)}
            id={field.name}
            name={field.name}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            value={field.state.value}
            {...props}
            type={isVisible ? 'text' : 'password'}
          />
          <button
            aria-label={t('toggleVisibility')}
            className="text-muted-foreground flex items-center justify-center px-2"
            onClick={toggleVisibility}
            tabIndex={-1}
            type="button"
          >
            {isVisible ? (
              <EyeClosed className="pointer-events-none" />
            ) : (
              <Eye className="pointer-events-none" />
            )}
          </button>
        </InputWrapper>
        <FieldInfo field={field} />
        {forgotPassword && (
          <Link
            className={cn(
              'text-primary self-end text-sm font-semibold',
              (isInvalid || props['aria-invalid']) && 'mt-0.5'
            )}
            href="/forgot-password"
          >
            {t('forgotPassword')}
          </Link>
        )}
      </div>
    </FieldWrapper>
  )
}
